/* ICCA delivery schedule v2: independent of delivery-date apps. */
(() => {
  'use strict';

  if (customElements.get('icca-delivery-schedule')) return;

  const DAY = 86400000;
  const PREFIX = 'ICCAお届け希望時間｜';
  const DATE_ATTR = '配送希望日';
  const YU_ATTR = 'ICCAゆうパック共通時間';

  const parse = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    const date = new Date(value + 'T00:00:00Z');
    return Number.isFinite(+date) &&
      date.toISOString().slice(0, 10) === value
      ? date
      : null;
  };

  const iso = date => date.toISOString().slice(0, 10);
  const add = (date, days) => new Date(+date + days * DAY);

  const tokyoToday = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());

    const get = type =>
      parts.find(part => part.type === type).value;

    return parse(`${get('year')}-${get('month')}-${get('day')}`);
  };

  const bundledHolidays = {
    "2026-01-01": "元日",
    "2026-01-12": "成人の日",
    "2026-02-11": "建国記念の日",
    "2026-02-23": "天皇誕生日",
    "2026-03-20": "春分の日",
    "2026-04-29": "昭和の日",
    "2026-05-03": "憲法記念日",
    "2026-05-04": "みどりの日",
    "2026-05-05": "こどもの日",
    "2026-05-06": "休日",
    "2026-07-20": "海の日",
    "2026-08-11": "山の日",
    "2026-09-21": "敬老の日",
    "2026-09-22": "休日",
    "2026-09-23": "秋分の日",
    "2026-10-12": "スポーツの日",
    "2026-11-03": "文化の日",
    "2026-11-23": "勤労感謝の日",
    "2027-01-01": "元日",
    "2027-01-11": "成人の日",
    "2027-02-11": "建国記念の日",
    "2027-02-23": "天皇誕生日",
    "2027-03-21": "春分の日",
    "2027-03-22": "休日",
    "2027-04-29": "昭和の日",
    "2027-05-03": "憲法記念日",
    "2027-05-04": "こどもの日",
    "2027-05-05": "こどもの日",
    "2027-07-19": "海の日",
    "2027-08-11": "山の日",
    "2027-09-20": "敬老の日",
    "2027-09-23": "秋分の日",
    "2027-10-11": "スポーツの日",
    "2027-11-03": "文化の日",
    "2027-11-23": "勤労感謝の日"
  };

  // Public holiday data is refreshed when available.
  // Bundled years work offline.
  let holidayPromise;

  function holidays() {
    if (!holidayPromise) {
      holidayPromise = (async () => {
        const data = { ...bundledHolidays };
        const coverage = new Set(['2026', '2027']);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 3500);

        try {
          const response = await fetch(
            'https://holidays-jp.github.io/api/v1/date.json',
            {
              signal: controller.signal,
              credentials: 'omit',
              referrerPolicy: 'no-referrer'
            }
          );

          if (response.ok) {
            const fresh = await response.json();

            for (const [key, name] of Object.entries(fresh)) {
              if (parse(key) && typeof name === 'string') {
                data[key] = name;
              }
            }

            const years = new Set(
              Object.keys(fresh).map(key => key.slice(0, 4))
            );

            for (const year of years) {
              if (
                Object.keys(fresh).filter(
                  key => key.startsWith(year + '-')
                ).length >= 16 &&
                fresh[year + '-01-01'] &&
                fresh[year + '-11-23']
              ) {
                coverage.add(year);
              }
            }
          }
        } catch (_) {
          // Use the verified bundled dates.
        } finally {
          clearTimeout(timer);
        }

        return { data, coverage };
      })();
    }

    return holidayPromise;
  }

  function calculate(config, today, holidayData) {
    const minimum = Number(config.min);
    const maximum = Number(config.max);

    if (
      !Number.isInteger(minimum) ||
      !Number.isInteger(maximum) ||
      minimum < 0 ||
      maximum < 1 ||
      minimum > maximum ||
      maximum > 730
    ) {
      throw new Error('受付日数の設定を確認してください。');
    }

    const closed = new Set();

    const closedLines = String(config.closed || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    for (const line of closedLines) {
      if (!parse(line)) {
        throw new Error(
          '臨時出荷不可日の日付形式を確認してください。'
        );
      }

      closed.add(line);
    }

    const start = config.vacation ? parse(config.start) : null;
    const end = config.vacation ? parse(config.end) : null;

    if (config.vacation && (!start || !end || start > end)) {
      throw new Error(
        '長期休暇の開始日・終了日を確認してください。'
      );
    }

    const isClosed = date => {
      const key = iso(date);

      if (!holidayData.coverage.has(key.slice(0, 4))) {
        throw new Error('祝日情報の更新が必要です。');
      }

      return (
        date.getUTCDay() === 0 ||
        date.getUTCDay() === 6 ||
        !!holidayData.data[key] ||
        (
          key.slice(5, 7) === '01' &&
          Number(key.slice(8)) <= 3
        ) ||
        closed.has(key) ||
        (
          config.vacation &&
          date >= start &&
          date <= end
        )
      );
    };

    let min = minimum === 0 ? iso(today) : null;
    const max = iso(add(today, maximum));
    let count = 0;

    for (let offset = 1; offset <= maximum && min === null; offset++) {
      const date = add(today, offset);

      if (!isClosed(date)) count++;

      if (count === minimum) {
        min = iso(date);
      }
    }

    if (!min || min > max) {
      throw new Error('受付日数を計算できませんでした。');
    }

    return { min, max };
  }

  const remembered = new Map();
  let serial = Promise.resolve();

  async function json(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 12000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(
          'Cart request failed: ' + response.status
        );
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error('Cart update failed');
      }

      return result;
    } finally {
      clearTimeout(timer);
    }
  }

  class DeliverySchedule extends HTMLElement {
    connectedCallback() {
      this.ready = false;
      this.error = false;
      this.pending = 0;

      this.config = JSON.parse(
        this.querySelector('[data-delivery-config]').textContent
      );

      this.date = this.querySelector('[data-delivery-date]');
      this.mode = this.querySelector('[data-delivery-mode]');
      this.dateAttribute = this.querySelector(
        '[data-date-attribute]'
      );
      this.status = this.querySelector('[data-delivery-status]');
      this.retry = this.querySelector('[data-delivery-retry]');
      this.dateRow = this.querySelector('[data-date-row]');

      this.fields = [
        ...this.querySelectorAll('[data-delivery-time]')
      ];

      for (const field of this.fields) {
        const value = remembered.get(field.name);

        if (
          value !== undefined &&
          [...field.options].some(
            option => option.value === value
          )
        ) {
          field.value = value;
        }
      }

      this.dateAttribute.value =
        remembered.get(DATE_ATTR) ?? this.dateAttribute.value;

      this.mode.value = parse(this.dateAttribute.value)
        ? 'date'
        : 'none';

      this.date.value = parse(this.dateAttribute.value)
        ? this.dateAttribute.value
        : '';

      this.onChange = event => {
        if (event.target === this.mode) {
          this.dateRow.hidden = this.mode.value !== 'date';
          this.date.disabled = this.mode.value !== 'date';

          if (this.mode.value === 'none') {
            this.date.value = '';
          }
        }

        if (
          event.target === this.mode ||
          event.target === this.date ||
          event.target.matches('[data-delivery-time]')
        ) {
          this.error = false;

          if (this.validate()) {
            this.save();
          } else {
            this.state();
          }
        }
      };

      this.onRetry = () => {
        if (this.ready) {
          this.save();
        } else {
          holidayPromise = undefined;
          this.initialize();
        }
      };

      this.addEventListener('change', this.onChange);
      this.retry.addEventListener('click', this.onRetry);

      this.onVisibility = () => {
        if (
          !document.hidden &&
          this.today !== iso(tokyoToday())
        ) {
          this.initialize();
        }
      };

      document.addEventListener(
        'visibilitychange',
        this.onVisibility
      );

      this.timer = setInterval(() => {
        if (this.today !== iso(tokyoToday())) {
          this.initialize();
        }
      }, 60000);

      this.initialize();
    }

    disconnectedCallback() {
      clearInterval(this.timer);

      document.removeEventListener(
        'visibilitychange',
        this.onVisibility
      );

      this.removeEventListener('change', this.onChange);
      this.retry.removeEventListener('click', this.onRetry);

      queueMicrotask(refreshLock);
    }

    async initialize() {
      this.ready = false;
      this.state();

      try {
        const today = tokyoToday();
        this.today = iso(today);

        this.range = calculate(
          this.config,
          today,
          await holidays()
        );

        if (!this.isConnected) return;

        this.date.min = this.range.min;
        this.date.max = this.range.max;

        const rangeText = this.querySelector(
          '[data-date-range]'
        );

        rangeText.style.whiteSpace = 'pre-line';
        rangeText.textContent =
          `最短ご指定可能日：${this.range.min.replaceAll('-', '/')}\n` +
          `最長ご指定可能日：${this.range.max.replaceAll('-', '/')}`;

        if (
          this.date.value &&
          (
            this.date.value < this.range.min ||
            this.date.value > this.range.max
          )
        ) {
          this.mode.value = 'none';
          this.date.value = '';

          this.querySelector('[data-date-notice]').textContent =
            '以前の希望日は受付期間外になったため「指定なし」に戻しました。';
        }

        this.dateRow.hidden = this.mode.value !== 'date';
        this.date.disabled = this.mode.value !== 'date';
        this.ready = true;
        this.error = false;

        this.save();
      } catch (error) {
        if (!this.isConnected) return;

        this.error = true;
        this.initializationError = error;
        this.state();

        console.error('[ICCA delivery setup]', error);
      }
    }

    validate() {
      if (!this.ready) return false;

      const valid =
        this.mode.value === 'none' ||
        (
          !!parse(this.date.value) &&
          this.date.value >= this.range.min &&
          this.date.value <= this.range.max
        );

      this.date.setCustomValidity(
        valid
          ? ''
          : '受付期間内のお届け希望日を選択するか「指定なし」を選んでください。'
      );

      this.invalid = !valid;

      if (valid) {
        this.dateAttribute.value =
          this.mode.value === 'none'
            ? '指定なし'
            : this.date.value;
      }

      return valid;
    }

    payload() {
      const attributes = {
        [DATE_ATTR]: this.dateAttribute.value
      };

      for (const field of this.fields) {
        attributes[field.name.slice(11, -1)] = field.value;
      }

      const group = this.querySelector('[data-yupack-common]');

      if (group) {
        const slot = group.value;

        for (
          const item of this.querySelectorAll(
            '[data-yupack-item]'
          )
        ) {
          const attributeName =
            item.dataset.attribute || PREFIX + item.dataset.key;

          attributes[attributeName] =
            `${item.dataset.title}｜ゆうパック｜${slot}`;
        }
      }

      for (const [key, value] of Object.entries(attributes)) {
        remembered.set(
          key === DATE_ATTR ? key : `attributes[${key}]`,
          value
        );
      }

      return attributes;
    }

    save() {
      if (!this.validate()) {
        this.state();
        return;
      }

      const attributes = this.payload();

      this.pending++;
      this.state();

      const task = async () => {
        if (!this.isConnected) return;

        const route = window.Shopify?.routes?.root || '/';
        const cart = await json(route + 'cart.js');
        const managed = new Set(Object.keys(attributes));

        for (const key of Object.keys(cart.attributes || {})) {
          if (
            (key.startsWith(PREFIX) || key === YU_ATTR) &&
            !managed.has(key)
          ) {
            attributes[key] = '';
          }
        }

        await json(route + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ attributes })
        });
      };

      const operation = serial.catch(() => {}).then(task);
      serial = operation;

      operation.then(
        () => {
          this.error = false;
        },
        error => {
          this.error = true;
          console.error('[ICCA delivery save]', error);
        }
      ).finally(() => {
        this.pending--;

        if (this.isConnected) {
          this.state();
        }
      });
    }

    state() {
      const blocked =
        !this.ready ||
        this.pending > 0 ||
        this.error ||
        this.invalid;

      this.dataset.blocked = String(!!blocked);

      this.status.textContent = this.error
        ? this.ready
          ? 'お届け希望を保存できませんでした。保存し直してください。'
          : 'お届け希望日の受付情報を確認できませんでした。再読み込みをお試しください。'
        : !this.ready
          ? '受付日を確認中…'
          : this.invalid
            ? '受付期間内の日付を選ぶか「指定なし」を選んでください。'
            : this.pending
              ? '保存中…'
              : '';

      this.retry.hidden = !this.error;

      refreshLock();
    }
  }

  function refreshLock() {
    const panel = document.querySelector(
      'icca-delivery-schedule'
    );

    const blocked =
      !!panel && panel.dataset.blocked !== 'false';

    document.documentElement.classList.toggle(
      'icca-delivery-blocked',
      blocked
    );

    for (
      const button of document.querySelectorAll(
        'main-cart [name="checkout"]'
      )
    ) {
      if (blocked && !button.disabled) {
        button.dataset.iccaDisabled = 'true';
        button.disabled = true;
      } else if (!blocked && button.dataset.iccaDisabled) {
        delete button.dataset.iccaDisabled;
        button.disabled = false;
      }
    }
  }

  document.addEventListener('submit', event => {
    const panel = document.querySelector(
      'icca-delivery-schedule'
    );

    if (event.target.id !== 'cart' || !panel) return;

    if (panel.today !== iso(tokyoToday())) {
      event.preventDefault();
      panel.initialize();
      return;
    }

    if (
      !panel.validate() ||
      panel.dataset.blocked !== 'false'
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();

      panel.state();

      panel.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });
    }
  }, true);

  customElements.define(
    'icca-delivery-schedule',
    DeliverySchedule
  );

  if (window.__ICCA_TEST__) {
    window.__iccaDeliveryTest = {
      parse,
      iso,
      add,
      calculate,
      tokyoToday,
      bundledHolidays
    };
  }
})();

/* 時間指定不可の商品を1つの欄にまとめる */
(() => {
  if (window.iccaUnavailableGrouped) return;
  window.iccaUnavailableGrouped = true;

  const initialized = new WeakSet();

  function groupUnavailable() {
    const panel = document.querySelector(
      'icca-delivery-schedule'
    );

    if (!panel || initialized.has(panel)) return;

    const selects = Array.from(
      panel.querySelectorAll('select[data-delivery-time]')
    ).filter(select => {
      const parts = select.value.split('｜');

      return (
        parts.length >= 3 &&
        parts[parts.length - 2] === '時間指定不可'
      );
    });

    if (!selects.length) return;

    initialized.add(panel);

    const names = selects.map(select => {
      const label = select.parentElement.querySelector('label');

      return (
        label?.firstChild?.textContent.trim() ||
        select.value.split('｜').slice(0, -2).join('｜')
      );
    });

    const firstRow = selects[0].parentElement;
    const label = firstRow.querySelector('label');

    if (label) {
      label.textContent = '時間指定不可の商品';
    }

    const list = document.createElement('ul');
    list.style.cssText =
      'font-size:12px;margin:8px 0;padding-left:16px;list-style:disc;';

    names.forEach(name => {
      const item = document.createElement('li');
      item.textContent = name;
      list.appendChild(item);
    });

    firstRow.insertBefore(list, selects[0]);

    const explanation = firstRow.querySelector('p');

    if (explanation) {
      explanation.textContent =
        '対象商品は時間帯を指定できません。';
    }

    selects.slice(1).forEach(select => {
      select.parentElement.style.setProperty(
        'display',
        'none',
        'important'
      );
    });
  }

  new MutationObserver(groupUnavailable).observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );

  groupUnavailable();
})();